import { faAnglesLeft } from '@fortawesome/free-solid-svg-icons';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import MatterService from '../../../AppSettings/services/MatterService';
import ToastService from '../../../AppSettings/services/ToastService';
import StatusBadge from '@frontend/Components/StatusBadge/StatusBadge';

import { isLegalEnvironment } from '../../../AppSettings/environmentConfig';
import useCurrentEmployee from '@frontend/AppSettings/hooks/useCurrentEmployee';
import useProjectDueDate from '@frontend/AppSettings/hooks/useProjectDueDate';
import UnifiedTopBar from '@frontend/Components/TopBar/UnifiedTopBar';
import { createDetailViewTemplate } from '@frontend/Components/TopBar/templates';
import Tabs from '@frontend/Components/Tabs';
import ConfirmationModal from '@frontend/Components/ConfirmationModal/ConfirmationModal';

interface MatterDetailsTopBarProps {
  setActivePage: (page: string) => void;
  activePage?: string;
  isTimesheetOnlyUser?: boolean;
}

const MatterDetailsTopBar: React.FC<MatterDetailsTopBarProps> = ({ 
  setActivePage, 
  activePage = 'general',
  isTimesheetOnlyUser = false
}) => {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createdDate, setCreatedDate] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [isEditingId, setIsEditingId] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [idValue, setIdValue] = useState('');
  const [nameValue, setNameValue] = useState('');
  const [pendingNewId, setPendingNewId] = useState<string | null>(null);
  const skipNameSaveRef = useRef(false);
  const isLegalEnv = isLegalEnvironment;

  // Projects (engineering) derive a due date from their latest milestone.
  const projectDueDate = useProjectDueDate(!isLegalEnv ? matterId : null);

  // Only permission level 1 (admin) can change IDs
  const currentEmployee = useCurrentEmployee();
  const rawPermishId = currentEmployee?.permish_id ?? null;
  const permishIdNum = typeof rawPermishId === 'string' ? parseInt(rawPermishId, 10) : rawPermishId;
  const canEditId = permishIdNum === 1;

  const { data: matterName } = useQuery({
    queryKey: ['MatterName', matterId],
    queryFn: () => MatterService.getMatterName(matterId!),
    enabled: !!matterId,
  });

  // Status options ordered from start to finish with colors
  const statusOptions = [
    { value: 'Status: Prospective', label: 'Prospective', color: '#9e9e9e' }, // Gray - earliest stage
    { value: 'Status: Not Started', label: 'Not Started', color: '#6c757d' }, // Dark gray - decided but not started
    { value: 'Status: In-progress', label: 'In progress', color: '#17a2b8' }, // Blue - actively working
    { value: 'Status: Redline', label: 'Redline', color: '#ff9800' }, // Orange - review/editing
    { value: 'Status: On Hold', label: 'On Hold', color: '#ffc107' }, // Yellow/Amber - paused
    { value: 'Status: Complete', label: 'Complete', color: '#28a745' }, // Green - finished
    { value: 'Status: Cancelled', label: 'Cancelled', color: '#dc3545' }, // Red - terminated
  ];

  // Get status color from statusOptions
  const getStatusColor = (status: string | null): string => {
    if (!status) return '#9e9e9e';
    // Remove "Status: " prefix if present for matching
    const cleanStatus = status.replace(/^Status:\s*/i, '');
    const option = statusOptions.find(opt => 
      opt.value.toLowerCase() === status.toLowerCase() ||
      opt.value.toLowerCase().replace(/^status:\s*/, '') === cleanStatus.toLowerCase()
    );
    return option ? option.color : '#9e9e9e';
  };

  // Get status variant for StatusBadge
  const getStatusVariant = (status: string | null): 'active' | 'at-risk' | 'in-review' | 'completed' | 'pending' | 'open' | 'closed' => {
    if (!status) return 'open';
    // Remove "Status: " prefix if present for matching
    const cleanStatus = status.replace(/^Status:\s*/i, '');
    const statusLower = cleanStatus.toLowerCase();
    
    if (statusLower.includes('complete') || statusLower === 'complete') {
      return 'completed';
    } else if (statusLower.includes('cancelled') || statusLower === 'cancelled') {
      return 'at-risk';
    } else if (statusLower.includes('progress') || statusLower.includes('in progress')) {
      return 'in-review';
    } else if (statusLower.includes('hold') || statusLower === 'on hold') {
      return 'pending';
    } else if (statusLower.includes('redline') || statusLower === 'redline') {
      return 'in-review';
    } else if (statusLower.includes('prospective') || statusLower === 'prospective') {
      return 'open';
    } else if (statusLower.includes('not started') || statusLower === 'not started') {
      return 'open';
    }
    
    // Fallback to existing logic
    if (statusLower.includes('active') || statusLower.includes('open')) {
      return 'active';
    } else if (statusLower.includes('risk') || statusLower.includes('overdue')) {
      return 'at-risk';
    } else if (statusLower.includes('review') || statusLower.includes('progress')) {
      return 'in-review';
    }
    
    return 'open';
  };

  const updateStatusMutation = useMutation({
    mutationFn: (newStatus: string) =>
      MatterService.updateMatter(matterId!, { status: newStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Matters', matterId!] });
      queryClient.invalidateQueries({ queryKey: ['MatterName', matterId!] });
      setIsEditingStatus(false);
      ToastService.success(isLegalEnv ? 'Status updated successfully' : 'Status updated successfully');
    },
    onError: (err: Error) => {
      console.error('Error updating status:', err);
      ToastService.error('Failed to update status. Please try again.');
    },
  });

  const updateNameMutation = useMutation({
    mutationFn: (newName: string) =>
      MatterService.updateMatter(matterId!, { mattername: newName }),
    onSuccess: (_data, newName) => {
      setIsEditingName(false);
      queryClient.setQueryData(
        ['MatterName', matterId],
        (prev: { mattername: string; sept_tenantid: string } | null | undefined) =>
          prev
            ? { ...prev, mattername: newName }
            : { mattername: newName, sept_tenantid: '' }
      );
      queryClient.invalidateQueries({ queryKey: ['Matters', matterId!] });
      ToastService.success(
        isLegalEnv ? 'Matter name updated successfully' : 'Project name updated successfully'
      );
    },
    onError: (err: Error) => {
      console.error('Error updating name:', err);
      ToastService.error(
        isLegalEnv
          ? 'Failed to update matter name. Please try again.'
          : 'Failed to update project name. Please try again.'
      );
    },
  });

  const renameMatterMutation = useMutation({
    mutationFn: (newMatterId: string) =>
      MatterService.renameMatter(matterId!, newMatterId),
    onSuccess: (_data, newMatterId) => {
      setIsEditingId(false);
      setPendingNewId(null);
      ToastService.success('Matter ID updated successfully');
      // The old ID is baked into many query keys; invalidate everything
      queryClient.invalidateQueries();
      navigate(
        `${isLegalEnv ? '/matterDetails' : '/projectDetails'}/${encodeURIComponent(newMatterId)}`,
        { replace: true }
      );
    },
    onError: (err: Error & { status?: number }) => {
      console.error('Error renaming matter:', err);
      setPendingNewId(null);
      if (err.status === 409) {
        ToastService.error('That matter ID is already in use.');
      } else {
        ToastService.error('Failed to update matter ID. Please try again.');
      }
    },
  });

  const startEditName = () => {
    if (isEditingId) return;
    skipNameSaveRef.current = false;
    setNameValue(matterName?.mattername || '');
    setIsEditingName(true);
  };

  const cancelEditName = () => {
    skipNameSaveRef.current = true;
    setIsEditingName(false);
    setNameValue('');
  };

  const saveName = () => {
    if (skipNameSaveRef.current) {
      skipNameSaveRef.current = false;
      return;
    }
    if (updateNameMutation.isPending) return;
    const next = nameValue.trim();
    if (!next || next === (matterName?.mattername || '').trim()) {
      setIsEditingName(false);
      setNameValue('');
      return;
    }
    updateNameMutation.mutate(next);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditName();
    }
  };

  const startEditId = () => {
    if (!canEditId) return;
    setIdValue(matterId ?? '');
    setIsEditingId(true);
  };

  const cancelEditId = () => {
    setIsEditingId(false);
    setPendingNewId(null);
  };

  const saveId = () => {
    const newId = idValue.trim();
    if (!newId || newId === matterId) {
      cancelEditId();
      return;
    }
    // Ask for confirmation before actually renaming
    setPendingNewId(newId);
  };

  const confirmRename = () => {
    if (pendingNewId) {
      renameMatterMutation.mutate(pendingNewId);
    }
  };

  const handleIdKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') saveId();
    if (e.key === 'Escape') cancelEditId();
  };

  useEffect(() => {
    const fetchMatterMeta = async () => {
      try {
        const details = await MatterService.getMatterDetails(matterId!);
        const openDate =
          (details as any)?.opendate ||
          (details as any)?.created_at ||
          (details as any)?.createdAt ||
          null;
        setCreatedDate(openDate ?? null);
        const matterStatus = (details as any)?.status || null;
        setStatus(matterStatus);
      } catch (error) {
        console.error('Error fetching matter details:', error);
        setCreatedDate(null);
        setStatus(null);
      }
    };
    fetchMatterMeta();
  }, [matterId]);

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    if (!selectedValue) {
      setIsEditingStatus(false);
      return;
    }
    
    // Strip "Status: " prefix if present for backend
    const statusToSave = selectedValue.replace(/^Status:\s*/i, '');
    const displayStatus = selectedValue; // Keep full value for display
    
    if (statusToSave && statusToSave !== status?.replace(/^Status:\s*/i, '')) {
      updateStatusMutation.mutate(statusToSave);
      setStatus(displayStatus);
    } else {
      setIsEditingStatus(false);
    }
  };

  const handleStatusBlur = () => {
    setIsEditingStatus(false);
  };

  const matterTitle = `${matterId} ${matterName?.mattername || 'Loading...'}`;

  const formatDate = (value?: string | null) => {
    if (!value) return null;
    try {
      return new Date(value).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return value;
    }
  };

  // Calendar-only strings (YYYY-MM-DD) must be built as local dates; otherwise
  // `new Date` parses them as UTC midnight and shows the prior day in -UTC zones.
  const formatDueDate = (value?: string | null) => {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const date = match
      ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      : new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Function to switch to a section
  const switchToSection = (sectionName: string) => {
    setActivePage(sectionName);
  };

  // For timesheet-only users, rename "Financial" to "Timesheets"
  const financialLabel = isTimesheetOnlyUser ? 'Timesheets' : 'Financial';

  const dropdownItems = isLegalEnv ? [
    { label: 'General', onClick: () => switchToSection('general') },
    { label: 'Tasks', onClick: () => switchToSection('tasks') },
    { label: financialLabel, onClick: () => switchToSection('financial') },
    { label: 'Files', onClick: () => switchToSection('files') },
    { label: 'Notes', onClick: () => switchToSection('notes') },
  ] : [
    { label: 'General', onClick: () => switchToSection('general') },
    { label: 'Checklist', onClick: () => switchToSection('tasks') },
    { label: financialLabel, onClick: () => switchToSection('financial') },
    { label: 'Files', onClick: () => switchToSection('files') },
    { label: 'Notes', onClick: () => switchToSection('notes') },
  ];

  const unifiedConfig = createDetailViewTemplate({
    leftExtras: [
      {
        type: 'button',
        icon: faAnglesLeft,
        onClick: () => navigate(isLegalEnv ? '/matters' : '/projects'),
        ariaLabel: 'Back to list',
      },
      <div className="matter-topbar__title" key="title-block">
        {isEditingName ? (
          <input
            className="matter-topbar__name matter-topbar__name-input"
            data-testid="matter-name-input"
            value={nameValue}
            maxLength={200}
            onChange={(e) => setNameValue(e.target.value)}
            onKeyDown={handleNameKeyDown}
            onBlur={saveName}
            disabled={updateNameMutation.isPending}
            autoFocus
            aria-label={isLegalEnv ? 'Edit matter name' : 'Edit project name'}
          />
        ) : (
          <div
            className="matter-topbar__name"
            data-testid="matter-name"
            onClick={startEditName}
            style={{ cursor: 'pointer' }}
            title={isLegalEnv ? 'Click to edit matter name' : 'Click to edit project name'}
          >
            {matterTitle}
          </div>
        )}
        <div className="matter-topbar__meta">
          {isEditingId ? (
            <span className="matter-topbar__id-edit">
              <input
                className="matter-topbar__pill matter-topbar__id-input"
                value={idValue}
                maxLength={50}
                onChange={(e) => setIdValue(e.target.value)}
                onKeyDown={handleIdKeyDown}
                disabled={renameMatterMutation.isPending}
                autoFocus
              />
              <button
                type="button"
                className="matter-topbar__id-button matter-topbar__id-button--save"
                onClick={saveId}
                disabled={renameMatterMutation.isPending}
              >
                Save
              </button>
              <button
                type="button"
                className="matter-topbar__id-button"
                onClick={cancelEditId}
                disabled={renameMatterMutation.isPending}
              >
                Cancel
              </button>
            </span>
          ) : (
            <span
              className="matter-topbar__pill"
              onClick={startEditId}
              style={canEditId ? { cursor: 'pointer' } : undefined}
              title={canEditId ? 'Click to edit matter ID' : undefined}
            >
              ID: {matterId}
            </span>
          )}
          {formatDate(createdDate) && (
            <span className="matter-topbar__pill">Created: {formatDate(createdDate)}</span>
          )}
          {!isLegalEnv && formatDueDate(projectDueDate) && (
            <span className="matter-topbar__pill">Due: {formatDueDate(projectDueDate)}</span>
          )}
          {isEditingStatus ? (
            <select
              className="matter-topbar__status-select"
              data-testid="matter-status-select"
              value={status || ''}
              onChange={handleStatusChange}
              onBlur={handleStatusBlur}
              autoFocus
            >
              <option value="">Select Status</option>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <div
              className="matter-topbar__status-wrapper"
              data-testid="matter-status-wrapper"
              onClick={() => setIsEditingStatus(true)}
              style={{ cursor: 'pointer', display: 'inline-flex' }}
              title="Click to edit status"
            >
              {status ? (
                <StatusBadge 
                  status={status} 
                  variant={getStatusVariant(status)} 
                  color={getStatusColor(status)} 
                  size="small" 
                />
              ) : (
                <span className="matter-topbar__status-placeholder">Status: Not Set</span>
              )}
            </div>
          )}
        </div>
      </div>,
    ],
    className: 'topBar matter-topbar',
    pageContext: {
      matterId,
      matterName: matterName?.mattername,
    },
  });

  // Map activePage to tab ID
  const currentTabId = activePage === 'tasks' && !isLegalEnv 
    ? 'checklist' 
    : (activePage === 'financial' && isTimesheetOnlyUser ? 'timesheets' : activePage);

  const tabs = dropdownItems.map((item) => ({
    id: item.label.toLowerCase(),
    label: item.label,
    onClick: item.onClick,
  }));

  const handleTabChange = (tabId: string) => {
    const match = tabs.find((t) => t.id === tabId);
    if (match) {
      match.onClick();
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={!!pendingNewId}
        onClose={() => setPendingNewId(null)}
        onConfirm={confirmRename}
        message={`Change ${isLegalEnv ? 'matter' : 'project'} ID from "${matterId}" to "${pendingNewId}"? All related records (tasks, billing, invoices, etc.) will be updated to the new ID.`}
        confirmLabel="Change ID"
        cancelLabel="Cancel"
      />
      <UnifiedTopBar {...unifiedConfig} />
      <div className="matter-tabs">
        <Tabs
          tabs={tabs.map(({ id, label }) => ({ id, label }))}
          activeTabId={currentTabId}
          onTabChange={handleTabChange}
          variant="underline"
        />
      </div>
    </>
  );
};

export default MatterDetailsTopBar;
